import Alert from "@mui/material/Alert";
import { Link } from "react-router-dom";

import { useAuth } from "@/providers";
import { ResourceView } from "@/shared/components/ResourceView";
import { type AgentRecord } from "@/shared/domain/agents";
import { useAgents } from "@/shared/domain/agents/hooks";
import {
  DataTable,
  createColumnHelper,
  useTable,
  type ColDef,
  type DataTableLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation";

import styles from "./AgentsTab.module.css";
import { CreateAgentMenu } from "./CreateAgentMenu";

const columnHelper = createColumnHelper<AgentRecord>();

function colId<T>(col: ColDef<T>): string {
  return col.colId ?? col.field ?? "";
}

/**
 * Module-scope ColDefs so AG Grid sees a stable identity across renders.
 * Navigation lives on the Name cell because ``DataTable`` doesn't expose
 * ``onRowClicked``. Every column sets ``hide: false`` to override the
 * primitive's ``defaultColDef.hide: true`` (consumer opt-in pattern; see
 * ``AgentThreadsTable``).
 */
const columns = [
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    sortable: true,
    hide: false,
    cellRenderer: (p: ICellRendererParams<AgentRecord, string | undefined>) => {
      const agent = p.data;
      if (!agent) {
        return null;
      }
      return (
        <Link
          to={Goto.createAgentRoute(agent.agent_id)}
          className={styles.nameLink}
        >
          {agent.name}
        </Link>
      );
    },
  }),
  columnHelper.accessor("description", {
    headerName: "Description",
    flex: 3,
    hide: false,
    valueFormatter: (p) => p.value ?? "",
  }),
  columnHelper.accessor("variables", {
    headerName: "Variables",
    width: 120,
    hide: false,
    // Helper restricts ``valueGetter``; rendering the count directly keeps
    // the same effect without dropping to a raw ``ColDef``.
    cellRenderer: (
      p: ICellRendererParams<AgentRecord, AgentRecord["variables"]>,
    ) => p.value?.length ?? 0,
  }),
  columnHelper.accessor("created_by", {
    headerName: "Created by",
    width: 180,
    sortable: true,
    hide: false,
  }),
  columnHelper.accessor("modified", {
    headerName: "Modified",
    width: 200,
    sortable: true,
    hide: false,
    valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleString() : ""),
  }),
];

/**
 * The "Agents" tab body, rendered inside the parent ``AgentsTabs``
 * container.
 *
 * Empty state is intentionally a custom card (rather than the table's
 * built-in ``noRowsContent`` overlay) because the CTA carries more weight
 * than a one-liner: the value prop for an empty org needs the full pitch,
 * not a centered table-cell overlay.
 */
export function AgentsTab() {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  const agentsQuery = useAgents({ orgId });
  const table = useTable<AgentRecord>({
    getRowId: (row) => row.agent_id,
    initialSort: { columnId: "modified", direction: "desc" },
    // Seed the hook's visible-columns mirror so a future column-picker
    // affordance reflects the actual mount-time visibility. Mirrors the
    // ``hide: false`` set on each ColDef above.
    defaultVisibleColumnIds: columns.map(colId),
  });

  const rows = agentsQuery.data?.items ?? [];

  let loading: DataTableLoadingState = "idle";
  if (agentsQuery.isLoading) {
    loading = "foreground";
  } else if (agentsQuery.isFetching) {
    loading = "background";
  }

  if (agentsQuery.isError) {
    return (
      <Alert severity="error" className={styles.errorBanner}>
        Failed to load agents. {agentsQuery.error?.message ?? ""}
      </Alert>
    );
  }

  if (!agentsQuery.isLoading && rows.length === 0) {
    return (
      <div className={styles.empty}>
        <h3 className={styles.emptyTitle}>No agents yet</h3>
        <p className={styles.emptyBody}>
          Agents capture a workflow once — system prompt, goals, label
          vocabulary — and let teammates launch it against new datasets without
          re-declaring the whole request. Create one to get started.
        </p>
        <CreateAgentMenu />
      </div>
    );
  }

  return (
    <>
      <div className={styles.toolbar}>
        <h2 className={styles.heading}>
          {rows.length} agent{rows.length === 1 ? "" : "s"}
        </h2>
        <CreateAgentMenu />
      </div>
      <div className={styles.tableSlot}>
        <ResourceView.Root>
          <DataTable<AgentRecord>
            table={table}
            // Backend doesn't return a count yet; leave totalRowCount undefined
            // so the footer hides the "1-N of M" label rather than implying a
            // count we don't have. ``hasNextPage`` mirrors the server's
            // ``next_token`` so the Next button is enabled iff more rows exist.
            hasNextPage={Boolean(agentsQuery.data?.next_token)}
            loading={loading}
            columns={columns}
            data={rows}
            noRowsContent="No agents match your filters"
          />
        </ResourceView.Root>
      </div>
    </>
  );
}
